import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Save, Trash2, FileText, Plus, Mail, KeyRound, X, UserPlus, Archive, Upload, Sparkles, Send, Bot, Loader2, Calendar, Plane, ChevronDown, ChevronUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { api, downloadPdf } from "@/api";
import { eur, pct, STARTING_PARAMS, MONTHS_IT } from "@/format";
import { OFFICE, OfficeCard, OfficeButton, OfficeField, OfficeInput, OfficeTextarea, VisibilityTag, StatusPill } from "@/pages/admin/officeUi";

const CUR_YEAR = new Date().getFullYear();

function Section({ title, children, visibility }) {
  return (
    <OfficeCard className="mt-6">
      <h3 className="font-display text-lg" style={{ color: OFFICE.ink }}>{title}</h3>
      <div className="mt-4">{children}</div>
      {visibility ? <VisibilityTag where={visibility} /> : null}
    </OfficeCard>
  );
}

function Select({ value, onChange, children, ...rest }) {
  return (
    <select value={value} onChange={onChange} {...rest} className="w-full rounded-xl border px-4 py-2.5 outline-none"
      style={{ borderColor: OFFICE.border, background: OFFICE.surface, color: OFFICE.ink }}>{children}</select>
  );
}

const emptyMonth = () => ({
  year: CUR_YEAR, month: new Date().getMonth() + 1,
  revenue: "", occupancy: "", adr: "", revpar: "", conversion: "", traffic: "",
  ota_quality: "", booking_position: "", revenue_target: "", review_score: "",
  ly_revenue: "", ly_occupancy: "", ly_adr: "", ly_revpar: "", ly_ota_quality: "", ly_booking_position: "", ly_review_score: "",
  competitor_adr: "", competitor_note: "", notes: "", price_points: [], notify_client: true,
});

// Map a saved period (period_out) into the editable form shape.
const periodToForm = (p) => ({
  year: p.year, month: p.month,
  revenue: p.revenue ?? "", occupancy: p.occupancy ?? "", adr: p.adr ?? "", revpar: p.revpar ?? "",
  conversion: p.conversion ?? "", traffic: p.traffic ?? "", ota_quality: p.ota_quality ?? "",
  booking_position: p.booking_position ?? "", revenue_target: p.revenue_target ?? "", review_score: p.review_score ?? "",
  ly_revenue: p.ly_revenue ?? "", ly_occupancy: p.ly_occupancy ?? "", ly_adr: p.ly_adr ?? "", ly_revpar: p.ly_revpar ?? "",
  ly_ota_quality: p.ly_ota_quality ?? "", ly_booking_position: p.ly_booking_position ?? "", ly_review_score: p.ly_review_score ?? "",
  competitor_adr: p.competitor_adr ?? "", competitor_note: p.competitor_note || "", notes: p.notes || "",
  price_points: p.price_points || [], notify_client: false,
});

// Accepts both "89.5" and "8,7" (and "1.234,56"): normalise to a JS number.
function parseNum(v) {
  if (v === null || v === undefined) return null;
  let s = String(v).trim();
  if (s === "") return null;
  const hasDot = s.includes("."), hasComma = s.includes(",");
  if (hasDot && hasComma) s = s.replace(/\./g, "").replace(",", ".");
  else if (hasComma) s = s.replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
// Keep only digits and a single decimal separator while typing.
const sanitizeDecimal = (v) => String(v).replace(/[^0-9.,]/g, "");

const numOrNull = (v) => parseNum(v);
const numOr0 = (v) => (parseNum(v) ?? 0);

// Stable, module-level field (defining it inside render caused focus loss).
function NumField({ label, value, onChange }) {
  return (
    <OfficeField label={label}>
      <OfficeInput
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(sanitizeDecimal(e.target.value))}
      />
    </OfficeField>
  );
}

export default function StructureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [structure, setStructure] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [starting, setStarting] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, p, sd] = await Promise.all([api.getStructure(id), api.adminPeriods(id, "monthly"), api.getStartingData(id)]);
      setStructure(s); setPeriods(p); setStarting(sd);
    } catch (e) { toast.error(e.message || "Errore"); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading || !structure) {
    return <div className="theme-office min-h-screen flex items-center justify-center" style={{ background: OFFICE.surface2, color: OFFICE.muted }}>Caricamento…</div>;
  }

  return (
    <div className="theme-office min-h-screen" style={{ background: OFFICE.surface2, color: OFFICE.ink }}>
      <header className="sticky top-0 z-30" style={{ background: OFFICE.surface, borderBottom: `1px solid ${OFFICE.border}` }}>
        <div className="max-w-4xl mx-auto px-5 md:px-6 h-16 flex items-center justify-between">
          <OfficeButton variant="ghost" onClick={() => navigate("/admin")}><ArrowLeft className="h-4 w-4" /> Portafoglio</OfficeButton>
          <OfficeButton onClick={() => navigate(`/admin/preview/${id}`)}><Eye className="h-4 w-4" /> Vedi come il cliente</OfficeButton>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-6 py-8 pb-20">
        <IdentitySection structure={structure} onSaved={load} />
        <FocusSection id={id} structure={structure} onSaved={load} />
        <NoteSection id={id} structure={structure} onSaved={load} />
        <StartingSection id={id} starting={starting} onSaved={load} />
        <CompetitorsSection id={id} structure={structure} onSaved={load} />
        <MonthSection id={id} periods={periods} structure={structure} onSaved={load} />
        <AnnualSection id={id} structure={structure} onSaved={load} />
        <AICopilotSection id={id} />
        <PricingStrategyLink id={id} />
        <OpportunitiesSection id={id} />
        <AccessSection id={id} structure={structure} onSaved={load} />
        <ArchiveSection id={id} onArchived={() => navigate("/admin")} />
      </main>
    </div>
  );
}

function IdentitySection({ structure, onSaved }) {
  const [name, setName] = useState(structure.name);
  const [location, setLocation] = useState(structure.location || "");
  const [busy, setBusy] = useState(false);

  const saveMeta = async () => {
    if (!name.trim()) { toast.error("Il nome non può essere vuoto"); return; }
    setBusy(true);
    try { await api.updateStructure(structure.id, { name: name.trim(), location: location.trim() }); toast.success("Struttura aggiornata"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const setField = async (field, value) => {
    try { await api.updateStructure(structure.id, { [field]: value }); onSaved(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <OfficeCard>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <OfficeField label="Nome struttura"><OfficeInput value={name} onChange={(e) => setName(e.target.value)} /></OfficeField>
          <div className="mt-3"><OfficeField label="Località"><OfficeInput value={location} onChange={(e) => setLocation(e.target.value)} /></OfficeField></div>
          <div className="mt-3"><OfficeButton onClick={saveMeta} disabled={busy}><Save className="h-4 w-4" /> Salva nome e località</OfficeButton></div>
        </div>
        <div className="w-full md:w-56 space-y-3">
          <OfficeField label="Stato">
            <Select value={structure.status} onChange={(e) => setField("status", e.target.value)}>
              <option value="COLLAUDO">In collaudo</option>
              <option value="OPERATIVA">Operativa</option>
            </Select>
          </OfficeField>
          <OfficeField label="Strategia prezzi">
            <Select value={structure.pricing_strategy} onChange={(e) => setField("pricing_strategy", e.target.value)}>
              <option value="IN_ATTIVAZIONE">In attivazione</option>
              <option value="ATTIVA">Attiva</option>
            </Select>
          </OfficeField>
          <div><StatusPill status={structure.status} /></div>
        </div>
      </div>
    </OfficeCard>
  );
}

function FocusSection({ id, structure, onSaved }) {
  const [text, setText] = useState(structure.current_focus?.text || "");
  const [reason, setReason] = useState(structure.current_focus?.reason || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.setFocus(id, { text, reason }); toast.success("Focus aggiornato"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Section title="Il nostro focus adesso" visibility="03 Il nostro focus adesso">
      <OfficeField label="Focus (breve)"><OfficeInput value={text} onChange={(e) => setText(e.target.value)} placeholder="Es. Aumentare la visibilità su Booking" /></OfficeField>
      <div className="mt-3"><OfficeField label="Perché (facoltativo)"><OfficeTextarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></OfficeField></div>
      <div className="mt-3"><OfficeButton onClick={save} disabled={busy}><Save className="h-4 w-4" /> Salva focus</OfficeButton></div>
    </Section>
  );
}

function NoteSection({ id, structure, onSaved }) {
  const [text, setText] = useState(structure.specialist_note?.text || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.setSpecialistNote(id, { text }); toast.success("Nota aggiornata"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Section title="Nota dello specialista" visibility="04 Cosa sta succedendo ora">
      <OfficeTextarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="La tua lettura del momento per il cliente…" />
      <div className="mt-3"><OfficeButton onClick={save} disabled={busy}><Save className="h-4 w-4" /> Salva nota</OfficeButton></div>
    </Section>
  );
}

function StartingSection({ id, starting, onSaved }) {
  const initParams = {};
  STARTING_PARAMS.forEach((p) => {
    const it = starting?.params?.[p.key];
    initParams[p.key] = { value: it?.value ?? "", urgency: it?.urgency || "low" };
  });
  const [params, setParams] = useState(initParams);
  const [notes, setNotes] = useState(starting?.notes || "");
  const [date, setDate] = useState(starting?.recorded_date || "");
  const [busy, setBusy] = useState(false);

  const setP = (key, field, val) => setParams((prev) => ({ ...prev, [key]: { ...prev[key], [field]: val } }));

  const save = async () => {
    setBusy(true);
    const outParams = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v.value !== "" && v.value !== null) outParams[k] = { value: parseNum(v.value), urgency: v.urgency };
    });
    try {
      await api.setStartingData(id, { params: outParams, notes, recorded_date: date || null });
      toast.success("Situazione iniziale salvata"); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Section title="Situazione iniziale" visibility="Situazione iniziale (cliente)">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {STARTING_PARAMS.map((p) => (
          <div key={p.key} className="rounded-xl border p-3" style={{ borderColor: OFFICE.border }}>
            <div className="text-sm font-medium mb-2" style={{ color: OFFICE.ink2 }}>{p.label} {p.unit ? <span style={{ color: OFFICE.muted }}>({p.unit})</span> : null}</div>
            <div className="flex gap-2">
              <OfficeInput type="text" inputMode="decimal" value={params[p.key].value} onChange={(e) => setP(p.key, "value", sanitizeDecimal(e.target.value))} placeholder="Valore" />
              <Select value={params[p.key].urgency} onChange={(e) => setP(p.key, "urgency", e.target.value)}>
                <option value="low">Bassa</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
              </Select>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <OfficeField label="Data rilevazione"><OfficeInput type="date" value={date} onChange={(e) => setDate(e.target.value)} /></OfficeField>
        <div className="md:col-span-2"><OfficeField label="La lettura del team"><OfficeTextarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></OfficeField></div>
      </div>
      <div className="mt-4"><OfficeButton onClick={save} disabled={busy}><Save className="h-4 w-4" /> Salva situazione iniziale</OfficeButton></div>
    </Section>
  );
}

function MonthSection({ id, periods, structure, onSaved }) {
  const [form, setForm] = useState(emptyMonth());
  const [busy, setBusy] = useState(false);
  const [editingKey, setEditingKey] = useState(null); // period_key currently loaded, or null = new
  const [forecastTargets, setForecastTargets] = useState(structure?.forecast_targets || {});
  const prefillRef = React.useRef({}); // last values we auto-applied from the forecast, per field
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pre-fill the month draft from the imported forecast: revenue -> "Obiettivo fatturato",
  // and occupancy/ADR/RevPAR -> the matching fields. Replaces previously auto-filled values
  // when the month changes, but never overwrites a value the admin typed or loaded.
  useEffect(() => {
    const key = `${form.year}-${String(form.month).padStart(2, "0")}`;
    const fc = forecastTargets[key] || null;
    const map = {
      revenue_target: fc && fc.revenue != null ? fc.revenue : null,
      occupancy: fc && fc.occupancy != null ? fc.occupancy : null,
      adr: fc && fc.adr != null ? fc.adr : null,
      revpar: fc && fc.revpar != null ? fc.revpar : null,
    };
    const updates = {};
    const applied = { ...prefillRef.current };
    for (const [field, val] of Object.entries(map)) {
      const newVal = val != null ? String(val) : "";
      const cur = form[field];
      const wasAuto = cur === "" || cur == null || cur === prefillRef.current[field];
      if (!wasAuto || cur === newVal) continue;
      updates[field] = newVal;
      applied[field] = newVal === "" ? null : newVal;
    }
    if (Object.keys(updates).length) {
      prefillRef.current = applied;
      setForm((f) => ({ ...f, ...updates }));
    }
  }, [form.year, form.month, forecastTargets]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadPeriod = (p) => {
    setForm(periodToForm(p));
    setEditingKey(p.period_key);
    window.scrollTo({ top: document.getElementById("month-form").offsetTop - 80, behavior: "smooth" });
  };

  const resetForm = () => { setForm(emptyMonth()); setEditingKey(null); };

  const [importing, setImporting] = useState(false);
  const fileRef = React.useRef(null);
  const onImportPdf = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const d = await api.importPeriodPdf(f);
      setForm((prev) => ({
        ...prev,
        year: d.year ?? prev.year,
        month: d.month ?? prev.month,
        revenue: d.revenue != null ? String(d.revenue) : prev.revenue,
        occupancy: d.occupancy != null ? String(d.occupancy) : prev.occupancy,
        adr: d.adr != null ? String(d.adr) : prev.adr,
        revpar: d.revpar != null ? String(d.revpar) : prev.revpar,
        ly_revenue: d.ly_revenue != null ? String(d.ly_revenue) : prev.ly_revenue,
        ly_occupancy: d.ly_occupancy != null ? String(d.ly_occupancy) : prev.ly_occupancy,
        ly_adr: d.ly_adr != null ? String(d.ly_adr) : prev.ly_adr,
        ly_revpar: d.ly_revpar != null ? String(d.ly_revpar) : prev.ly_revpar,
        notify_client: false,
      }));
      setEditingKey(null);
      toast.success(`Dati estratti da "${d.period_label || "PDF"}" — controlla e salva`);
    } catch (err) {
      toast.error(err.message || "Estrazione non riuscita");
    } finally {
      setImporting(false);
    }
  };

  const [importingForecast, setImportingForecast] = useState(false);
  const forecastRef = React.useRef(null);
  const onImportForecast = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImportingForecast(true);
    try {
      const d = await api.importForecastXlsx(id, f);
      setForecastTargets(d.targets || {});
      toast.success(`Forecast caricato: obiettivi impostati per ${d.months_count} mesi`);
      onSaved();
    } catch (err) {
      toast.error(err.message || "Import forecast non riuscito");
    } finally {
      setImportingForecast(false);
    }
  };

  const addPP = () => set("price_points", [...form.price_points, { label: "", price: "" }]);
  const setPP = (i, field, val) => set("price_points", form.price_points.map((pp, idx) => idx === i ? { ...pp, [field]: val } : pp));
  const rmPP = (i) => set("price_points", form.price_points.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    const body = {
      period_type: "monthly", year: Number(form.year), month: Number(form.month),
      revenue: numOr0(form.revenue), occupancy: numOr0(form.occupancy), adr: numOr0(form.adr), revpar: numOr0(form.revpar),
      conversion: numOr0(form.conversion), traffic: numOr0(form.traffic),
      ota_quality: numOrNull(form.ota_quality), booking_position: numOrNull(form.booking_position),
      revenue_target: numOrNull(form.revenue_target), review_score: numOrNull(form.review_score),
      ly_revenue: numOrNull(form.ly_revenue), ly_occupancy: numOrNull(form.ly_occupancy), ly_adr: numOrNull(form.ly_adr),
      ly_revpar: numOrNull(form.ly_revpar), ly_ota_quality: numOrNull(form.ly_ota_quality),
      ly_booking_position: numOrNull(form.ly_booking_position), ly_review_score: numOrNull(form.ly_review_score),
      competitor_adr: numOrNull(form.competitor_adr), competitor_note: form.competitor_note, notes: form.notes,
      price_points: form.price_points.filter((pp) => pp.label && pp.price !== "").map((pp) => ({ label: pp.label, price: parseNum(pp.price) })),
      notify_client: !!form.notify_client,
    };
    try {
      const res = await api.upsertPeriod(id, body);
      const sent = res.emails_sent || 0;
      toast.success(sent > 0 ? `Mese salvato · ${sent} email inviata/e al cliente` : "Mese salvato");
      // Keep the just-saved month loaded so the admin can review/correct it.
      setForm(periodToForm(res));
      setEditingKey(res.period_key);
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const del = async (pid) => {
    if (!window.confirm("Eliminare questo mese?")) return;
    try { await api.deletePeriod(id, pid); toast.success("Mese eliminato"); onSaved(); } catch (e) { toast.error(e.message); }
  };
  const pdf = async (p) => {
    try { await downloadPdf(`/admin/structures/${id}/periods/${p.id}/report.pdf`, `YieldUp-${p.period_key}.pdf`); }
    catch { toast.error("Download non riuscito"); }
  };

  return (
    <>
      {/* Saved months */}
      <Section title="Mesi salvati" visibility="01 / 02 / 05 / 06 e PDF">
        {periods.length === 0 ? <p style={{ color: OFFICE.muted }}>Nessun mese salvato.</p> : (
          <div className="space-y-2">
            {periods.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border p-3" style={{ borderColor: OFFICE.border }}>
                <div>
                  <div className="font-medium" style={{ color: OFFICE.ink }}>{p.period_label}</div>
                  <div className="text-sm" style={{ color: OFFICE.muted }}>{eur(p.revenue)} · occ. {pct(p.occupancy)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => loadPeriod(p)}>Modifica</OfficeButton>
                  <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => pdf(p)}><FileText className="h-4 w-4" /> PDF</OfficeButton>
                  <button onClick={() => del(p.id)} className="h-9 w-9 rounded-full flex items-center justify-center" style={{ border: `1px solid #E7C4BB`, color: "#C0341A" }}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Month form */}
      <div id="month-form">
        <Section title="Inserisci / aggiorna un mese" visibility="01 / 02 / 05 / 06 e PDF">
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl px-4 py-2.5" style={{ background: editingKey ? OFFICE.brandTertiary : OFFICE.surface3, border: `1px solid ${OFFICE.border}` }}>
            <span className="text-sm font-medium" style={{ color: editingKey ? OFFICE.brandStrong : OFFICE.muted }}>
              {editingKey ? "Stai modificando un mese salvato — le modifiche sovrascrivono i dati esistenti" : "Nuovo mese"}
            </span>
            {editingKey ? (
              <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={resetForm}><Plus className="h-4 w-4" /> Nuovo mese</OfficeButton>
            ) : null}
          </div>

          {/* Import from PDF */}
          <div className="mb-5 rounded-xl border p-4" style={{ borderColor: OFFICE.border, background: OFFICE.brandTertiary }}>
            <input ref={fileRef} type="file" accept="application/pdf" onChange={onImportPdf} style={{ display: "none" }} />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-medium" style={{ color: OFFICE.ink }}>Compila da PDF del gestionale</p>
                <p className="text-xs mt-0.5" style={{ color: OFFICE.muted }}>
                  Carica il report "Vendita": fatturato, occupazione, ADR e RevPAR vengono compilati in automatico. Controlla e salva.
                </p>
              </div>
              <OfficeButton onClick={() => fileRef.current && fileRef.current.click()} disabled={importing} data-testid="import-pdf-btn">
                <Upload className="h-4 w-4" /> {importing ? "Lettura in corso…" : "Carica PDF"}
              </OfficeButton>
            </div>
          </div>

          {/* Import forecast (Excel) → monthly revenue goals */}
          <div className="mb-5 rounded-xl border p-4" style={{ borderColor: OFFICE.border, background: OFFICE.surface3 }}>
            <input ref={forecastRef} type="file" accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onImportForecast} style={{ display: "none" }} />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-medium" style={{ color: OFFICE.ink }}>Obiettivi da forecast (Excel)</p>
                <p className="text-xs mt-0.5" style={{ color: OFFICE.muted }}>
                  Carica il file forecast: l'obiettivo di fatturato di ogni mese e i valori previsti di occupazione, ADR e RevPAR vengono impostati in automatico. Selezionando un mese, i campi si precompilano (controlla e correggi).
                  {Object.keys(forecastTargets).length > 0 ? ` Forecast caricato per ${Object.keys(forecastTargets).length} mesi.` : ""}
                </p>
              </div>
              <OfficeButton variant="ghost" onClick={() => forecastRef.current && forecastRef.current.click()} disabled={importingForecast} data-testid="import-forecast-btn">
                <Upload className="h-4 w-4" /> {importingForecast ? "Lettura in corso…" : "Carica forecast"}
              </OfficeButton>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <OfficeField label="Anno"><OfficeInput type="number" value={form.year} onChange={(e) => set("year", e.target.value)} /></OfficeField>
            <OfficeField label="Mese">
              <Select value={form.month} onChange={(e) => set("month", e.target.value)}>
                {MONTHS_IT.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </Select>
            </OfficeField>
            <NumField label="Fatturato (€)" value={form.revenue} onChange={(v) => set("revenue", v)} />
            <NumField label="Occupazione (%)" value={form.occupancy} onChange={(v) => set("occupancy", v)} />
            <NumField label="ADR (€)" value={form.adr} onChange={(v) => set("adr", v)} />
            <NumField label="RevPAR (€)" value={form.revpar} onChange={(v) => set("revpar", v)} />
            <NumField label="Conversion (%)" value={form.conversion} onChange={(v) => set("conversion", v)} />
            <NumField label="Traffico" value={form.traffic} onChange={(v) => set("traffic", v)} />
            <NumField label="Qualità OTA (1-10)" value={form.ota_quality} onChange={(v) => set("ota_quality", v)} />
            <NumField label="Posizione Booking (#)" value={form.booking_position} onChange={(v) => set("booking_position", v)} />
            <NumField label="Recensioni (0-10)" value={form.review_score} onChange={(v) => set("review_score", v)} />
            <NumField label="Obiettivo fatturato (€)" value={form.revenue_target} onChange={(v) => set("revenue_target", v)} />
          </div>

          {/* Previous-year reference (for +/- vs anno precedente) */}
          <div className="mt-6 rounded-xl border p-4" style={{ borderColor: OFFICE.border, background: OFFICE.surface3 }}>
            <p className="text-sm font-medium" style={{ color: OFFICE.ink2 }}>Confronto anno precedente (facoltativo)</p>
            <p className="text-xs mt-1 mb-3" style={{ color: OFFICE.muted }}>
              Inserisci i valori dello stesso mese dell'anno scorso: il cliente vedrà i +/- vs anno precedente.
              Se hai già caricato il mese dell'anno scorso, il confronto è automatico e puoi lasciare vuoto qui.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <NumField label="Fatturato anno prec. (€)" value={form.ly_revenue} onChange={(v) => set("ly_revenue", v)} />
              <NumField label="Occupazione anno prec. (%)" value={form.ly_occupancy} onChange={(v) => set("ly_occupancy", v)} />
              <NumField label="ADR anno prec. (€)" value={form.ly_adr} onChange={(v) => set("ly_adr", v)} />
              <NumField label="RevPAR anno prec. (€)" value={form.ly_revpar} onChange={(v) => set("ly_revpar", v)} />
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium" style={{ color: OFFICE.ink2 }}>Andamento prezzi (facoltativo)</span>
              <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={addPP}><Plus className="h-4 w-4" /> Aggiungi</OfficeButton>
            </div>
            <div className="mt-3 space-y-2">
              {form.price_points.map((pp, i) => (
                <div key={i} className="flex gap-2">
                  <OfficeInput value={pp.label} onChange={(e) => setPP(i, "label", e.target.value)} placeholder="Etichetta (es. Weekend)" />
                  <OfficeInput type="text" inputMode="decimal" value={pp.price} onChange={(e) => setPP(i, "price", sanitizeDecimal(e.target.value))} placeholder="Prezzo €" />
                  <button onClick={() => rmPP(i)} className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center" style={{ border: `1px solid ${OFFICE.border}`, color: OFFICE.muted }}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <OfficeField label="Competitor ADR (€) — solo team"><OfficeInput type="text" inputMode="decimal" value={form.competitor_adr} onChange={(e) => set("competitor_adr", sanitizeDecimal(e.target.value))} /></OfficeField>
            <OfficeField label="Nota competitor — solo team"><OfficeInput value={form.competitor_note} onChange={(e) => set("competitor_note", e.target.value)} /></OfficeField>
          </div>

          <div className="mt-4"><OfficeField label="Nota del mese · visibile al cliente"><OfficeTextarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></OfficeField></div>

          <label className="mt-4 flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.notify_client} onChange={(e) => set("notify_client", e.target.checked)} className="h-4 w-4 accent-[#D9501F]" />
            <span className="text-sm" style={{ color: OFFICE.ink2 }}>Avvisa il cliente via email (nuovi dati del mese)</span>
          </label>

          <div className="mt-5"><OfficeButton onClick={submit} disabled={busy}><Save className="h-4 w-4" /> {busy ? "Salvataggio…" : "Salva mese"}</OfficeButton></div>
        </Section>
      </div>
    </>
  );
}

function PricingStrategyLink({ id }) {
  const navigate = useNavigate();
  return (
    <OfficeCard className="mt-6" data-testid="pricing-strategy-link">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg,#1F9E73,#1F6FE0)" }}>
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-display text-lg" style={{ color: OFFICE.ink }}>Strategia Prezzi</h3>
            <p className="text-xs" style={{ color: OFFICE.muted }}>Crea con l'AI la strategia tariffaria dell'anno dai dati storici — pavimento prezzi, note dettagliate e commento generale</p>
          </div>
        </div>
        <OfficeButton onClick={() => navigate(`/admin/structures/${id}/strategia`)} data-testid="open-pricing-btn">
          <Sparkles className="h-4 w-4" /> Apri Strategia Prezzi
        </OfficeButton>
      </div>
    </OfficeCard>
  );
}

function PricingStrategySection_UNUSED({ id }) {
  const CUR = new Date().getFullYear();
  const [year, setYear] = useState(CUR + 1);
  const [units, setUnits] = useState([""]);
  const [premium, setPremium] = useState("10");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const loadYear = useCallback(async (y) => {
    try {
      const d = await api.pricingGet(id, y);
      setRows((d.rows || []).map((r) => ({ ...r })));
      setUnits(d.units && d.units.length ? d.units : [""]);
      if (d.weekend_premium != null) setPremium(String(Math.round(d.weekend_premium * 100)));
    } catch { /* noop */ }
  }, [id]);
  useEffect(() => { loadYear(year); }, [year, loadYear]);

  const setUnit = (i, v) => setUnits((u) => u.map((x, k) => (k === i ? v : x)));
  const addUnit = () => setUnits((u) => [...u, ""]);
  const rmUnit = (i) => setUnits((u) => u.filter((_, k) => k !== i));
  const setCell = (idx, key, v) => setRows((rs) => rs.map((r, k) => (k === idx ? { ...r, [key]: v } : r)));

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const unitList = units.map((u) => u.trim()).filter(Boolean);
    setBusy("generate");
    try {
      const { job_id } = await api.pricingGenerate(id, year, unitList, (parseFloat(premium) || 0) / 100, f);
      let done = false;
      for (let i = 0; i < 40 && !done; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const st = await api.pricingJob(id, job_id);
        if (st.status === "done") {
          setRows((st.result.rows || []).map((r) => ({ ...r })));
          if (st.result.units?.length) setUnits(st.result.units);
          toast.success("Strategia generata! Controlla e salva.");
          done = true;
        } else if (st.status === "error") {
          toast.error("Generazione non riuscita, riprova"); done = true;
        }
      }
      if (!done) toast.error("La generazione sta impiegando troppo, riprova");
    } catch (err) { toast.error(err.message || "Errore"); }
    finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.pricingSave(id, year, {
        units: units.map((u) => u.trim()).filter(Boolean),
        weekend_premium: (parseFloat(premium) || 0) / 100,
        rows: rows.map((r) => ({
          month: Number(r.month), unit: r.unit || "", band: r.band || "",
          bar_weekday: r.bar_weekday === "" || r.bar_weekday == null ? null : Number(r.bar_weekday),
          bar_weekend: r.bar_weekend === "" || r.bar_weekend == null ? null : Number(r.bar_weekend),
          note: r.note || "",
        })),
        client_note: "",
      });
      toast.success("Strategia salvata");
    } catch (e) { toast.error(e.message || "Errore nel salvataggio"); }
    finally { setBusy(""); }
  };

  const byMonth = {};
  rows.forEach((r, idx) => { (byMonth[r.month] = byMonth[r.month] || []).push({ ...r, _idx: idx }); });

  return (
    <OfficeCard className="mt-6" data-testid="pricing-strategy">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg,#1F9E73,#1F6FE0)" }}>
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-display text-lg" style={{ color: OFFICE.ink }}>Strategia Prezzi</h3>
          <p className="text-xs" style={{ color: OFFICE.muted }}>Carica i dati dell'anno scorso e l'AI crea la strategia tariffaria (feriale/weekend) mese per mese</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <OfficeField label="Anno strategia"><OfficeInput type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="pricing-year" /></OfficeField>
        <OfficeField label="Premio weekend (%)"><OfficeInput value={premium} onChange={(e) => setPremium(e.target.value)} data-testid="pricing-premium" /></OfficeField>
      </div>

      <div className="mt-4">
        <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: OFFICE.muted }}>Unità / tipologie</div>
        <div className="space-y-2">
          {units.map((u, i) => (
            <div key={i} className="flex gap-2">
              <OfficeInput value={u} onChange={(e) => setUnit(i, e.target.value)} placeholder="Es. Villa intera, Camera Deluxe, Suite…" data-testid={`pricing-unit-${i}`} />
              {units.length > 1 ? <OfficeButton variant="ghost" onClick={() => rmUnit(i)}><X className="h-4 w-4" /></OfficeButton> : null}
            </div>
          ))}
        </div>
        <OfficeButton variant="ghost" className="mt-2" onClick={addUnit} data-testid="pricing-add-unit"><Plus className="h-4 w-4" /> Aggiungi unità</OfficeButton>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.pdf" onChange={onFile} style={{ display: "none" }} />
        <OfficeButton onClick={() => fileRef.current && fileRef.current.click()} disabled={busy === "generate"} data-testid="pricing-generate-btn">
          {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy === "generate" ? "L'AI sta creando la strategia…" : "Carica dati e genera con AI"}
        </OfficeButton>
        {rows.length ? <OfficeButton variant="ghost" onClick={save} disabled={busy === "save"} data-testid="pricing-save-btn"><Save className="h-4 w-4" /> Salva strategia</OfficeButton> : null}
      </div>

      {rows.length ? (
        <div className="mt-5 overflow-x-auto" data-testid="pricing-table">
          {MONTHS_IT.map((mName, mi) => {
            const list = byMonth[mi + 1] || [];
            if (!list.length) return null;
            return (
              <div key={mi} className="mb-5">
                <div className="font-display text-sm mb-2" style={{ color: OFFICE.ink }}>{mName}</div>
                <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ color: OFFICE.muted }}>
                      <th className="text-left font-mono text-[10px] uppercase pb-1">Unità</th>
                      <th className="text-left font-mono text-[10px] uppercase pb-1 w-28">Fascia</th>
                      <th className="text-left font-mono text-[10px] uppercase pb-1 w-24">Feriale €</th>
                      <th className="text-left font-mono text-[10px] uppercase pb-1 w-24">Weekend €</th>
                      <th className="text-left font-mono text-[10px] uppercase pb-1">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => (
                      <tr key={r._idx} style={{ borderTop: `1px solid ${OFFICE.border}` }}>
                        <td className="pr-2 py-1" style={{ color: OFFICE.ink }}>{r.unit}</td>
                        <td className="pr-2"><OfficeInput value={r.band || ""} onChange={(e) => setCell(r._idx, "band", e.target.value)} /></td>
                        <td className="pr-2"><OfficeInput value={r.bar_weekday ?? ""} onChange={(e) => setCell(r._idx, "bar_weekday", e.target.value)} data-testid={`pricing-weekday-${r._idx}`} /></td>
                        <td className="pr-2"><OfficeInput value={r.bar_weekend ?? ""} onChange={(e) => setCell(r._idx, "bar_weekend", e.target.value)} /></td>
                        <td><OfficeInput value={r.note || ""} onChange={(e) => setCell(r._idx, "note", e.target.value)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      ) : null}
      <VisibilityTag where="Admin · la vista cliente con conferma arriverà nel passo successivo" />
    </OfficeCard>
  );
}

function AICopilotSection({ id }) {
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [caText, setCaText] = useState("");
  const [caPublished, setCaPublished] = useState(false);
  const [caBusy, setCaBusy] = useState(false);
  useEffect(() => {
    api.getClientAnalysis(id).then((d) => { setCaText(d.text || ""); setCaPublished(!!d.published); }).catch(() => {});
  }, [id]);
  const saveClientAnalysis = async (published) => {
    setCaBusy(true);
    try {
      await api.setClientAnalysis(id, { text: caText, published });
      setCaPublished(published);
      toast.success(published ? "Analisi pubblicata nella dashboard del cliente" : "Analisi salvata (non pubblicata)");
    } catch (e) { toast.error(e.message || "Operazione non riuscita"); }
    finally { setCaBusy(false); }
  };
  const unpublishClientAnalysis = async () => {
    setCaBusy(true);
    try { await api.setClientAnalysis(id, { text: caText, published: false }); setCaPublished(false); toast.success("Analisi rimossa dalla dashboard del cliente"); }
    catch (e) { toast.error(e.message || "Operazione non riuscita"); }
    finally { setCaBusy(false); }
  };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.aiChatHistory(id).then(setMessages).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const d = await api.aiAnalyze(id);
      setAnalysis(d);
    } catch (e) { toast.error(e.message || "Analisi non disponibile"); }
    finally { setAnalyzing(false); }
  };

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setSending(true);
    try {
      const d = await api.aiChat(id, q);
      setMessages((m) => [...m, { role: "assistant", text: d.reply }]);
    } catch (e) {
      toast.error(e.message || "Risposta non disponibile");
      setMessages((m) => [...m, { role: "assistant", text: "Scusa, non riesco a rispondere in questo momento. Riprova." }]);
    } finally { setSending(false); }
  };

  const impactColor = (imp) => imp === "alto" ? "#4F9D69" : imp === "medio" ? "#C98A2B" : OFFICE.muted;

  return (
    <OfficeCard className="mt-6" data-testid="ai-copilot">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg,#E0531F,#8B2FB0)" }}>
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-display text-lg" style={{ color: OFFICE.ink }}>Copilota AI</h3>
          <p className="text-xs" style={{ color: OFFICE.muted }}>Analizza i dati e chiedi consigli su prezzi, offerte e andamento</p>
        </div>
      </div>

      <div className="mt-4">
        <OfficeButton onClick={analyze} disabled={analyzing} data-testid="ai-analyze-btn">
          {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {analyzing ? "Sto analizzando i dati…" : "Genera analisi"}
        </OfficeButton>
      </div>

      {analysis ? (
        <div className="mt-4 space-y-4" data-testid="ai-analysis">
          {analysis.summary ? (
            <div className="rounded-xl p-4 text-sm leading-relaxed ai-prose" style={{ background: OFFICE.surface3, color: OFFICE.ink, border: `1px solid ${OFFICE.border}` }}>
              <ReactMarkdown>{analysis.summary}</ReactMarkdown>
            </div>
          ) : null}
          {(analysis.actions || []).length ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {analysis.actions.map((a, i) => (
                <div key={i} className="rounded-xl p-4" style={{ background: OFFICE.surface, border: `1px solid ${OFFICE.border}` }} data-testid={`ai-action-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ color: impactColor(a.impact), border: `1px solid ${impactColor(a.impact)}` }}>{a.impact || "—"}</span>
                    <span className="text-[10px]" style={{ color: OFFICE.muted }}>Azione {i + 1}</span>
                  </div>
                  <div className="mt-2 font-medium text-sm" style={{ color: OFFICE.ink }}>{a.title}</div>
                  <div className="mt-1 text-xs" style={{ color: OFFICE.muted }}>{a.detail}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6">
        <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: OFFICE.muted }}>Chiedi al copilota</div>
        <div ref={scrollRef} className="rounded-xl p-3 max-h-80 overflow-y-auto space-y-3" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}` }} data-testid="ai-chat">
          {messages.length === 0 && !sending ? (
            <p className="text-sm py-6 text-center" style={{ color: OFFICE.muted }}>Es. "Come sta andando settembre rispetto all'anno scorso?" oppure "Su quali mesi devo spingere i prezzi?"</p>
          ) : null}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`rounded-2xl px-3 py-2 text-sm max-w-[85%] ai-prose`} style={{
                background: m.role === "user" ? OFFICE.ink : OFFICE.surface,
                color: m.role === "user" ? "#fff" : OFFICE.ink,
                border: m.role === "user" ? "none" : `1px solid ${OFFICE.border}`,
              }}>
                {m.role === "assistant" ? <ReactMarkdown>{m.text}</ReactMarkdown> : m.text}
              </div>
            </div>
          ))}
          {sending ? (
            <div className="flex justify-start">
              <div className="rounded-2xl px-3 py-2 text-sm flex items-center gap-2" style={{ background: OFFICE.surface, color: OFFICE.muted, border: `1px solid ${OFFICE.border}` }}>
                <Bot className="h-4 w-4" /> <Loader2 className="h-3 w-3 animate-spin" /> sto pensando…
              </div>
            </div>
          ) : null}
        </div>
        <div className="mt-2 flex gap-2">
          <OfficeInput value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Scrivi la tua domanda…" data-testid="ai-chat-input" />
          <OfficeButton onClick={send} disabled={sending || !input.trim()} data-testid="ai-chat-send"><Send className="h-4 w-4" /></OfficeButton>
        </div>
      </div>
      <div className="mt-6 rounded-xl p-4" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}` }} data-testid="client-analysis-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-display text-base" style={{ color: OFFICE.ink }}>Analisi del tuo specialista (per il cliente)</div>
            <p className="text-xs mt-0.5" style={{ color: OFFICE.muted }}>Scrivi (o incolla dal Copilota) un'analisi chiara e pubblicala nella dashboard del proprietario.</p>
          </div>
          <span className="rounded-full px-3 py-1 text-xs font-medium"
            style={caPublished ? { background: "#E6F2EA", color: "#2E8B4F" } : { background: "#F5F3EF", color: OFFICE.muted }}
            data-testid="client-analysis-status">
            {caPublished ? "Pubblicata al cliente" : "Non pubblicata"}
          </span>
        </div>
        {analysis?.summary ? (
          <button onClick={() => setCaText(analysis.summary)} className="mt-3 text-xs underline" style={{ color: OFFICE.brand }} data-testid="client-analysis-use-ai">
            Usa il riepilogo del Copilota qui sotto
          </button>
        ) : null}
        <div className="mt-2">
          <OfficeTextarea rows={5} value={caText} onChange={(e) => setCaText(e.target.value)} data-testid="client-analysis-text"
            placeholder="Es. Questo trimestre il fatturato è cresciuto del 12%… Ti consiglio di…" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {caPublished ? (
            <>
              <OfficeButton onClick={() => saveClientAnalysis(true)} disabled={caBusy || !caText.trim()} data-testid="client-analysis-update"><Save className="h-4 w-4" /> Aggiorna pubblicata</OfficeButton>
              <OfficeButton variant="danger" onClick={unpublishClientAnalysis} disabled={caBusy}><X className="h-4 w-4" /> Nascondi al cliente</OfficeButton>
            </>
          ) : (
            <>
              <OfficeButton onClick={() => saveClientAnalysis(true)} disabled={caBusy || !caText.trim()} data-testid="client-analysis-publish"><Eye className="h-4 w-4" /> Pubblica al cliente</OfficeButton>
              <OfficeButton variant="ghost" onClick={() => saveClientAnalysis(false)} disabled={caBusy || !caText.trim()}><Save className="h-4 w-4" /> Salva bozza</OfficeButton>
            </>
          )}
        </div>
      </div>

      <VisibilityTag where="Solo admin · uno strumento per te e il tuo team" />
    </OfficeCard>
  );
}

const OPP_KINDS = ["festività", "ponte", "festa locale", "evento", "voli", "stagione"];

function OpportunitiesSection({ id }) {
  const CUR = new Date().getFullYear();
  const [year, setYear] = useState(CUR + 1);
  const [items, setItems] = useState([]);
  const [air, setAir] = useState({});
  const [status, setStatus] = useState("draft");
  const [busy, setBusy] = useState("");
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async (y) => {
    try {
      const d = await api.getOpportunities(id, y);
      setItems((d.items || []).map((it) => ({ ...it })));
      setAir(d.air_connectivity || {});
      setStatus(d.status || "draft");
    } catch { /* noop */ }
  }, [id]);
  useEffect(() => { load(year); }, [year, load]);

  const setItem = (i, k, v) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addItem = () => setItems((arr) => [...arr, { month: 1, label: "", title: "", kind: "evento", detail: "", impact: "medio", start_date: "", end_date: "", surcharge_pct: "" }]);
  const rmItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const generate = async () => {
    setBusy("generate");
    try {
      const { items: gen, air_connectivity } = await api.generateOpportunities(id, year);
      setItems((gen || []).map((it) => ({ ...it })));
      setAir(air_connectivity || {});
      toast.success("Opportunità generate dall'AI — controlla, modifica e salva");
    } catch (e) { toast.error(e.message || "Generazione non riuscita"); }
    finally { setBusy(""); }
  };

  const refreshAir = async () => {
    setBusy("air");
    try {
      const air_connectivity = await api.adminAirRefresh(id, year);
      setAir(air_connectivity || {});
      toast.success("Check voli aggiornato e salvato");
    } catch (e) { toast.error(e.message || "Aggiornamento voli non riuscito"); }
    finally { setBusy(""); }
  };

  const save = async (publish) => {
    setBusy("save");
    try {
      const clean = items
        .filter((it) => (it.title || "").trim())
        .map((it) => ({
          month: Number(it.month) || 1, label: it.label || "", title: it.title || "",
          kind: it.kind || "", detail: it.detail || "", impact: it.impact || "",
          start_date: it.start_date || null, end_date: it.end_date || null,
          surcharge_pct: it.surcharge_pct === "" || it.surcharge_pct == null ? null : Number(it.surcharge_pct),
        }));
      const st = publish ? "published" : "draft";
      await api.setOpportunities(id, year, { items: clean, air_connectivity: air, status: st });
      setStatus(st);
      toast.success(publish ? "Opportunità pubblicate al cliente" : "Opportunità salvate (bozza)");
    } catch (e) { toast.error(e.message || "Salvataggio non riuscito"); }
    finally { setBusy(""); }
  };

  const impactColor = (imp) => imp === "alto" ? "#4F9D69" : imp === "medio" ? "#C98A2B" : OFFICE.muted;
  const airColor = (lvl) => lvl === "alta" ? "#4F9D69" : lvl === "media" ? "#C98A2B" : "#9A8F7E";

  return (
    <OfficeCard className="mt-6" data-testid="opportunities-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg,#1F9E73,#C98A2B)" }}>
            <Calendar className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-display text-lg" style={{ color: OFFICE.ink }}>Opportunità di Vendita {year}</h3>
            <p className="text-xs" style={{ color: OFFICE.muted }}>Festività, ponti, feste locali, eventi e afflusso aereo: i periodi su cui spingere, con l'aiuto dell'AI</p>
          </div>
        </div>
        <span className="rounded-full px-3 py-1 text-xs font-medium"
          style={status === "published" ? { background: "#E6F2EA", color: "#2E8B4F" } : { background: "#F5F3EF", color: OFFICE.muted }}
          data-testid="opportunities-status">
          {status === "published" ? "Pubblicate" : "Bozza"}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 items-end">
        <div className="w-32"><OfficeField label="Anno"><OfficeInput type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="opportunities-year" /></OfficeField></div>
        <OfficeButton onClick={generate} disabled={busy === "generate"} data-testid="opportunities-generate-btn">
          {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy === "generate" ? "L'AI sta analizzando occasioni e voli…" : "Genera con AI"}
        </OfficeButton>
        <OfficeButton variant="ghost" onClick={refreshAir} disabled={busy === "air"} data-testid="air-refresh-btn">
          {busy === "air" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plane className="h-4 w-4" />} Aggiorna check voli
        </OfficeButton>
      </div>

      {air && air.airport ? (
        <div className="mt-4 rounded-xl p-3" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}` }} data-testid="air-connectivity">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: OFFICE.brand }}>
            <Plane className="h-4 w-4" /> Afflusso aereo · {air.airport}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {MONTHS_IT.map((m, idx) => {
              const lvl = (air.by_month || {})[String(idx + 1)] || "—";
              return (
                <div key={idx} className="rounded-lg px-2 py-1 text-center" style={{ border: `1px solid ${OFFICE.border}`, minWidth: 54 }}>
                  <div className="text-[10px]" style={{ color: OFFICE.muted }}>{m.slice(0, 3)}</div>
                  <div className="text-[11px] font-medium capitalize" style={{ color: airColor(lvl) }}>{lvl}</div>
                </div>
              );
            })}
          </div>
          {air.note ? <p className="mt-2 text-xs" style={{ color: OFFICE.ink2 }}>{air.note}</p> : null}
        </div>
      ) : null}

      {items.length ? (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: OFFICE.muted }}>{items.length} opportunità</span>
            <button onClick={() => setCollapsed((c) => !c)} className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: OFFICE.brand }} data-testid="opportunities-collapse-toggle">
              {collapsed ? <><ChevronDown className="h-4 w-4" /> Espandi per modificare</> : <><ChevronUp className="h-4 w-4" /> Comprimi elenco</>}
            </button>
          </div>
          {collapsed ? (
            <div className="space-y-1.5" data-testid="opportunities-compact">
              {items.map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2" style={{ borderColor: OFFICE.border, background: OFFICE.surface3 }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-mono uppercase" style={{ color: OFFICE.muted, minWidth: 30 }}>{(MONTHS_IT[(Number(it.month) || 1) - 1] || "").slice(0, 3)}</span>
                    <span className="text-sm truncate" style={{ color: OFFICE.ink }}>{it.title || "—"}</span>
                    {it.label ? <span className="text-xs truncate" style={{ color: OFFICE.muted }}>· {it.label}</span> : null}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {it.surcharge_pct !== "" && it.surcharge_pct != null ? <span className="text-[10px] font-mono" style={{ color: OFFICE.brand }}>+{it.surcharge_pct}%</span> : null}
                    <span className="text-[10px] font-mono uppercase" style={{ color: impactColor(it.impact) }}>{it.impact}</span>
                    <button onClick={() => rmItem(i)} className="h-7 w-7 rounded-full flex items-center justify-center" style={{ border: `1px solid ${OFFICE.border}`, color: OFFICE.muted }}><X className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div className="space-y-3" data-testid="opportunities-list">
          {items.map((it, i) => (
            <div key={i} className="rounded-xl p-3" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}` }} data-testid={`opportunity-${i}`}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                <OfficeField label="Mese">
                  <select value={it.month} onChange={(e) => setItem(i, "month", Number(e.target.value))}
                    className="w-full rounded-xl border px-3 py-2.5 outline-none" style={{ background: OFFICE.surface, borderColor: OFFICE.border, color: OFFICE.ink }}>
                    {MONTHS_IT.map((m, idx) => <option key={idx} value={idx + 1}>{m}</option>)}
                  </select>
                </OfficeField>
                <OfficeField label="Periodo"><OfficeInput value={it.label} onChange={(e) => setItem(i, "label", e.target.value)} placeholder="24-26 apr" /></OfficeField>
                <div className="md:col-span-2"><OfficeField label="Occasione"><OfficeInput value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} placeholder="Ponte del 25 aprile" /></OfficeField></div>
                <OfficeField label="Tipo">
                  <select value={it.kind} onChange={(e) => setItem(i, "kind", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2.5 outline-none" style={{ background: OFFICE.surface, borderColor: OFFICE.border, color: OFFICE.ink }}>
                    {OPP_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </OfficeField>
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
                <OfficeField label="Data inizio"><OfficeInput type="date" value={it.start_date || ""} onChange={(e) => setItem(i, "start_date", e.target.value)} data-testid={`opp-start-${i}`} /></OfficeField>
                <OfficeField label="Data fine"><OfficeInput type="date" value={it.end_date || ""} onChange={(e) => setItem(i, "end_date", e.target.value)} /></OfficeField>
                <OfficeField label="Sovrapprezzo %" hint="sui giorni dell'evento"><OfficeInput value={it.surcharge_pct ?? ""} onChange={(e) => setItem(i, "surcharge_pct", e.target.value)} data-testid={`opp-surcharge-${i}`} placeholder="es. 20" /></OfficeField>
                <OfficeField label="Impatto">
                  <select value={it.impact} onChange={(e) => setItem(i, "impact", e.target.value)}
                    className="w-full rounded-xl border px-3 py-2.5 outline-none" style={{ background: OFFICE.surface, borderColor: impactColor(it.impact), color: impactColor(it.impact) }}>
                    {["alto", "medio", "basso"].map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </OfficeField>
              </div>
              <div className="mt-2 flex items-end gap-2">
                <div className="flex-1"><OfficeField label="Consiglio operativo"><OfficeTextarea rows={2} value={it.detail} onChange={(e) => setItem(i, "detail", e.target.value)} placeholder="Minimo 3 notti, apri 4-5 mesi prima, +15% sul BAR." /></OfficeField></div>
                <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => rmItem(i)}><X className="h-4 w-4" /></OfficeButton>
              </div>
            </div>
          ))}
          </div>
          )}
        </div>
      ) : (
        <p className="mt-4 text-sm" style={{ color: OFFICE.muted }}>Nessuna opportunità per quest'anno. Genera con l'AI o aggiungile a mano.</p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <OfficeButton variant="ghost" onClick={addItem}><Plus className="h-4 w-4" /> Aggiungi opportunità</OfficeButton>
        <OfficeButton onClick={() => save(true)} disabled={busy === "save"} data-testid="opportunities-publish-btn"><Eye className="h-4 w-4" /> Salva e pubblica</OfficeButton>
        <OfficeButton variant="ghost" onClick={() => save(false)} disabled={busy === "save"} data-testid="opportunities-save-btn"><Save className="h-4 w-4" /> Salva bozza</OfficeButton>
        {items.length ? <OfficeButton variant="ghost" onClick={() => downloadPdf(`/admin/structures/${id}/opportunities/${year}/pdf`, `YieldUp-Opportunita-${year}.pdf`).catch(() => toast.error("Salva prima le opportunità"))} data-testid="opportunities-pdf-btn"><FileText className="h-4 w-4" /> Scarica PDF</OfficeButton> : null}
      </div>
      <VisibilityTag where="Opportunità (se pubblicate)" />
    </OfficeCard>
  );
}


function AnnualSection({ id, structure, onSaved }) {
  const CUR = new Date().getFullYear();
  const yearsFromData = Object.keys(structure.annual_data || {}).map(Number);
  const [year, setYear] = useState(CUR);
  const [lyRevenue, setLyRevenue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const a = (structure.annual_data || {})[String(year)] || {};
    setLyRevenue(a.ly_revenue != null ? String(a.ly_revenue) : "");
    setNote(a.note || "");
  }, [year, structure]);

  const save = async () => {
    setBusy(true);
    try {
      await api.setAnnual(id, year, { revenue: null, ly_revenue: parseNum(lyRevenue), note });
      toast.success("Dati annuali salvati");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const yearOptions = [...new Set([CUR - 1, CUR, CUR + 1, ...yearsFromData])].sort((a, b) => b - a);

  return (
    <Section title="Dati annuali" visibility="Colpo d'occhio annuale + nota dell'anno (cliente)">
      <p className="text-sm mb-3" style={{ color: OFFICE.muted }}>
        Inserisci il <b>fatturato totale dell'anno precedente</b> e una nota per l'anno. Il fatturato dell'anno in corso si calcola in automatico sommando i mesi che compili, così vedi man mano quanto manca per raggiungere (o superare) l'anno scorso.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <OfficeField label="Anno">
          <Select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="annual-year-select">
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
        </OfficeField>
        <NumField label="Fatturato totale anno precedente (€)" value={lyRevenue} onChange={setLyRevenue} />
      </div>
      <div className="mt-4">
        <OfficeField label="Nota dell'anno · visibile al cliente">
          <OfficeTextarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </OfficeField>
      </div>
      <div className="mt-4"><OfficeButton onClick={save} disabled={busy} data-testid="save-annual-btn"><Save className="h-4 w-4" /> {busy ? "Salvataggio…" : "Salva dati annuali"}</OfficeButton></div>
    </Section>
  );
}

function CompetitorsSection({ id, structure, onSaved }) {
  const [rows, setRows] = useState((structure.competitors || []).map((c) => ({ name: c.name || "", adr: c.adr ?? "", note: c.note || "" })));
  const [busy, setBusy] = useState(false);

  const add = () => setRows([...rows, { name: "", adr: "", note: "" }]);
  const upd = (i, field, val) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const rm = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    const clean = rows
      .filter((r) => r.name.trim())
      .map((r) => ({ name: r.name.trim(), adr: parseNum(r.adr), note: r.note.trim() }));
    try {
      await api.setCompetitors(id, clean);
      toast.success("Elenco competitor salvato");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const avg = (() => {
    const vals = rows.map((r) => parseNum(r.adr)).filter((v) => v !== null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  })();

  return (
    <Section title="Competitor selezionati" visibility="Competitor sul mercato (cliente)">
      <p className="text-sm mb-3" style={{ color: OFFICE.muted }}>
        Elenco dei competitor che hai selezionato con il loro ADR. Questi dati sono visibili al cliente. {avg !== null ? `ADR medio competitor: ${eur(avg)}.` : ""}
      </p>
      <div className="space-y-2">
        {rows.length === 0 ? <p className="text-sm" style={{ color: OFFICE.muted }}>Nessun competitor. Aggiungine uno.</p> : rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-5"><OfficeInput value={r.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Nome competitor" /></div>
            <div className="col-span-3"><OfficeInput type="text" inputMode="decimal" value={r.adr} onChange={(e) => upd(i, "adr", sanitizeDecimal(e.target.value))} placeholder="ADR loro €" /></div>
            <div className="col-span-3"><OfficeInput value={r.note} onChange={(e) => upd(i, "note", e.target.value)} placeholder="Nota (facoltativa)" /></div>
            <div className="col-span-1 flex justify-center">
              <button onClick={() => rm(i)} className="h-9 w-9 rounded-full flex items-center justify-center" style={{ border: `1px solid ${OFFICE.border}`, color: OFFICE.muted }}><X className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <OfficeButton variant="ghost" onClick={add}><Plus className="h-4 w-4" /> Aggiungi competitor</OfficeButton>
        <OfficeButton onClick={save} disabled={busy}><Save className="h-4 w-4" /> {busy ? "Salvataggio…" : "Salva elenco"}</OfficeButton>
      </div>
    </Section>
  );
}

function AccessSection({ id, structure, onSaved }) {
  const clients = structure.clients || [];
  const [newEmail, setNewEmail] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState({}); // uid -> {email?, pw?}

  const create = async () => {
    if (!newEmail.trim() || newPw.length < 6) { toast.error("Email valida e password (min 6 caratteri)"); return; }
    setBusy(true);
    try {
      const res = await api.createClient({ email: newEmail.trim(), password: newPw, structure_id: id });
      toast.success(res.email_sent ? "Accesso creato · email di benvenuto inviata" : "Accesso creato (email non inviata)");
      setNewEmail(""); setNewPw(""); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const resetPw = async (uid) => {
    const pw = editing[uid]?.pw || "";
    if (pw.length < 6) { toast.error("Password minimo 6 caratteri"); return; }
    try { const r = await api.resetClientPassword(uid, pw); toast.success(r.email_sent ? "Password reimpostata · email inviata" : "Password reimpostata"); setEditing((s) => ({ ...s, [uid]: {} })); }
    catch (e) { toast.error(e.message); }
  };
  const changeEmail = async (uid) => {
    const email = editing[uid]?.email || "";
    if (!email.trim()) { toast.error("Email non valida"); return; }
    try { await api.updateClientEmail(uid, email.trim()); toast.success("Email aggiornata"); setEditing((s) => ({ ...s, [uid]: {} })); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  const resend = async (uid) => { try { await api.resendWelcome(uid); toast.success("Email di benvenuto reinviata"); } catch (e) { toast.error(e.message); } };
  const remove = async (uid) => { if (!window.confirm("Rimuovere l'accesso a questa struttura?")) return; try { const r = await api.deleteClient(uid, id); toast.success(r.unlinked ? "Accesso a questa struttura rimosso (l'account resta attivo sulle altre)" : "Accesso rimosso"); onSaved(); } catch (e) { toast.error(e.message); } };
  const [linkEmail, setLinkEmail] = useState("");
  const link = async () => {
    if (!linkEmail.trim()) { toast.error("Inserisci l'email dell'account esistente"); return; }
    try { await api.linkClient(id, linkEmail.trim()); toast.success("Account collegato: ora vede anche questa struttura"); setLinkEmail(""); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  const setEdit = (uid, field, val) => setEditing((s) => ({ ...s, [uid]: { ...s[uid], [field]: val } }));

  return (
    <Section title="Accessi cliente" visibility="team">
      <div className="space-y-3">
        {clients.length === 0 ? <p style={{ color: OFFICE.muted }}>Nessun accesso cliente. Creane uno qui sotto.</p> :
          clients.map((c) => (
            <div key={c.id} className="rounded-xl border p-4" style={{ borderColor: OFFICE.border }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2"><Mail className="h-4 w-4" style={{ color: OFFICE.brand }} /><span className="font-medium" style={{ color: OFFICE.ink }}>{c.email}</span></div>
                <div className="flex items-center gap-2 flex-wrap">
                  <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => resend(c.id)}><Mail className="h-4 w-4" /> Reinvia email</OfficeButton>
                  <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => setEdit(c.id, "showPw", !editing[c.id]?.showPw)}><KeyRound className="h-4 w-4" /> Reset password</OfficeButton>
                  <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => setEdit(c.id, "showEmail", !editing[c.id]?.showEmail)}>Cambia email</OfficeButton>
                  <OfficeButton variant="danger" className="!py-1.5 !px-3" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4" /> Rimuovi</OfficeButton>
                </div>
              </div>
              {editing[c.id]?.showPw ? (
                <div className="mt-3 flex gap-2">
                  <OfficeInput type="text" value={editing[c.id]?.pw || ""} onChange={(e) => setEdit(c.id, "pw", e.target.value)} placeholder="Nuova password (min 6)" />
                  <OfficeButton onClick={() => resetPw(c.id)}>Applica</OfficeButton>
                </div>
              ) : null}
              {editing[c.id]?.showEmail ? (
                <div className="mt-3 flex gap-2">
                  <OfficeInput type="email" value={editing[c.id]?.email || ""} onChange={(e) => setEdit(c.id, "email", e.target.value)} placeholder="Nuova email" />
                  <OfficeButton onClick={() => changeEmail(c.id)}>Applica</OfficeButton>
                </div>
              ) : null}
            </div>
          ))}
      </div>

      <div className="mt-5 rounded-xl border p-4" style={{ borderColor: OFFICE.border, background: OFFICE.surface3 }}>
        <div className="flex items-center gap-2 mb-3"><UserPlus className="h-4 w-4" style={{ color: OFFICE.brand }} /><span className="font-medium" style={{ color: OFFICE.ink }}>Nuovo accesso</span></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <OfficeInput type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@cliente.it" />
          <OfficeInput type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Password iniziale (min 6)" />
          <OfficeButton onClick={create} disabled={busy}><Plus className="h-4 w-4" /> {busy ? "Creazione…" : "Crea accesso"}</OfficeButton>
        </div>
        <p className="text-xs mt-2" style={{ color: OFFICE.muted }}>Il cliente riceve un'email di benvenuto con un link per scegliere la propria password.</p>
      </div>

      <div className="mt-3 rounded-xl border p-4" style={{ borderColor: OFFICE.border, background: OFFICE.surface3 }} data-testid="link-client-box">
        <div className="flex items-center gap-2 mb-3"><Mail className="h-4 w-4" style={{ color: OFFICE.brand }} /><span className="font-medium" style={{ color: OFFICE.ink }}>Collega un account esistente</span></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2"><OfficeInput type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="email già usata su un'altra struttura" data-testid="link-client-email" /></div>
          <OfficeButton onClick={link} data-testid="link-client-btn">Collega account</OfficeButton>
        </div>
        <p className="text-xs mt-2" style={{ color: OFFICE.muted }}>Per proprietari con più strutture: con lo stesso login sceglieranno quale struttura vedere.</p>
      </div>
    </Section>
  );
}

function ArchiveSection({ id, onArchived }) {
  const archive = async () => {
    if (!window.confirm("Archiviare la struttura? Gli accessi dei clienti verranno disattivati.")) return;
    try { await api.archiveStructure(id); toast.success("Struttura archiviata"); onArchived(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="mt-8 flex justify-end">
      <OfficeButton variant="danger" onClick={archive}><Archive className="h-4 w-4" /> Archivia struttura</OfficeButton>
    </div>
  );
}
